import React, { useState, useEffect } from 'react';
import { WCSupplier } from '../../types/wordpress';
import { WordPressAPI } from '../../services/api/api';
import SupplierForm from './SupplierForm';
import { Button, Table, Modal, message, Popconfirm, Tag, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, DeleteOutlined, UndoOutlined, SearchOutlined } from '@ant-design/icons';


const SupplierList: React.FC = () => {
    const [suppliers, setSuppliers] = useState<WCSupplier[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState<WCSupplier | null>(null);
    const [searchText, setSearchText] = useState('');
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        fetchSuppliers();
    }, [currentPage, pageSize, searchText]);

    const fetchSuppliers = async () => {
        try {
            const data = await WordPressAPI.getSuppliers({
                page: currentPage,
                per_page: pageSize,
                search: searchText
            });
            setSuppliers(data.items);
            setTotal(data.total);
        } catch (error) {
            message.error('Failed to fetch suppliers');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getFilteredSuppliers = () => {
        return suppliers.filter(supplier =>
            supplier.company_name.toLowerCase().includes(searchText.toLowerCase()) ||
            supplier.contact_name.toLowerCase().includes(searchText.toLowerCase()) ||
            supplier.email.toLowerCase().includes(searchText.toLowerCase()) ||
            supplier.phone.includes(searchText)
        );
    };

    const handleSoftDelete = async (id: number) => {
        try {
            await WordPressAPI.updateSupplier(id, { is_deleted: true });
            message.success('Supplier deactivated successfully');
            fetchSuppliers();
        } catch (error) {
            message.error('Failed to deactivate supplier');
            console.error(error);
        }
    };

    const handleRestore = async (id: number) => {
        try {
            await WordPressAPI.updateSupplier(id, { is_deleted: false });
            message.success('Supplier restored successfully');
            fetchSuppliers();
        } catch (error) {
            message.error('Failed to restore supplier');
            console.error(error);
        }
    };



    const handleEdit = (supplier: WCSupplier) => {
        setEditingSupplier(supplier);
        setIsModalVisible(true);
    };

    const handleAddNew = () => {
        setEditingSupplier(null);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingSupplier(null);
    };

    const handleFormSubmit = async (values: Partial<WCSupplier>) => {
        try {
            if (editingSupplier) {
                await WordPressAPI.updateSupplier(editingSupplier.id, values);
                message.success('Supplier updated successfully');
            } else {
                await WordPressAPI.createSupplier(values);
                message.success('Supplier created successfully');
            }
            setIsModalVisible(false);
            fetchSuppliers();
        } catch (error) {
            message.error('Failed to save supplier');
            console.error(error);
        }
    };

    const columns: ColumnsType<WCSupplier> = [
        {
            title: 'Company Name',
            dataIndex: 'company_name',
            key: 'company_name',
            sorter: (a, b) => a.company_name.localeCompare(b.company_name),
        },
        {
            title: 'Contact Name',
            dataIndex: 'contact_name',
            key: 'contact_name',
            sorter: (a, b) => a.contact_name.localeCompare(b.contact_name),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => a.email.localeCompare(b.email),
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            sorter: (a, b) => a.phone.localeCompare(b.phone),
        },
        {
            title: 'Status',
            key: 'is_deleted',
            filters: [
                { text: 'Active', value: 'active' },
                { text: 'Inactive', value: 'inactive' },
            ],
            onFilter: (value, record) => {
                if (value === 'active') {
                    return !record.is_deleted;
                }
                return record.is_deleted;
            },
            render: (_, record) => (
                record.is_deleted ?
                    <Tag color="red">Inactive</Tag> :
                    <Tag color="green">Active</Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <span className="flex flex-col sm:flex-row gap-2">
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        className="whitespace-nowrap"
                    >
                        Edit
                    </Button>
                    {record.is_deleted ? (
                        <Popconfirm
                            title="Restore this supplier?"
                            onConfirm={() => handleRestore(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="primary"
                                ghost
                                icon={<UndoOutlined />}
                                className="whitespace-nowrap"
                            >
                                Restore
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Deactivate this supplier?"
                            onConfirm={() => handleSoftDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                ghost
                                icon={<DeleteOutlined />}
                                className="whitespace-nowrap"
                            >
                                Deactivate
                            </Button>
                        </Popconfirm>
                    )}
                </span>
            ),
        },
    ];

    return (
        <div className="w-full p-4">
            <div className="mb-6 space-y-4 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
                <h2 className="text-2xl font-semibold">Suppliers</h2>
                <div className="flex flex-col sm:flex-row gap-4">
                    <Input
                        placeholder="Search suppliers..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        className="w-full sm:w-[300px]"
                        allowClear
                    />
                    <Button
                        type="primary"
                        onClick={handleAddNew}
                        className="whitespace-nowrap"
                    >
                        Add New Supplier
                    </Button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table
                    columns={columns}
                    dataSource={getFilteredSuppliers()}
                    loading={loading}
                    rowKey="id"
                    rowClassName={(record) => record.is_deleted ? 'opacity-50' : ''}
                    pagination={{
                        total: total,
                        current: currentPage,
                        pageSize: pageSize,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </div>

            <Modal
                title={editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={800}
                className="responsive-modal"
                destroyOnClose={true}
            >
                <SupplierForm
                    key={editingSupplier?.id || 'new'}
                    initialValues={editingSupplier}
                    onSubmit={handleFormSubmit}
                    onCancel={handleModalClose}
                />
            </Modal>
        </div>
    );
};

export default SupplierList; 